import { css } from "dx-styles";
import { useEffect, useRef, useState } from "react";

import { api } from "../api/client";
import type { FormulaCheck } from "../api/types";
import type { Parameter, ParameterValue } from "../lib/document";
import { errorText, muted, row, stack } from "./primitives";
import { tokens } from "../theme/tokens";

const input = css({
  minHeight: "34px",
  padding: `0 ${tokens.space.sm}`,
  borderRadius: tokens.radius.sm,
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.paper,
  width: "100%",
  fontFamily: tokens.font.mono,
});

const invalid = css({ borderColor: tokens.color.signal });

/// The caret line under the offending character. Monospace on both, so column
/// N in the input is column N here.
const caret = css({
  fontFamily: tokens.font.mono,
  color: tokens.color.signal,
  whiteSpace: "pre",
  fontSize: "13px",
  lineHeight: 1.2,
});

const value = css({
  fontFamily: tokens.font.mono,
  backgroundColor: tokens.color.kraft,
  border: `1px solid ${tokens.color.rule}`,
  borderRadius: tokens.radius.sm,
  padding: `0 ${tokens.space.xs}`,
});

const suggestions = css({ display: "flex", gap: tokens.space.xs, flexWrap: "wrap" });

const suggestion = css({
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.paper,
  borderRadius: "999px",
  padding: `0 ${tokens.space.sm}`,
  fontSize: "12px",
  cursor: "pointer",
  fontFamily: tokens.font.mono,
});

/** Parameter values as the preview panel would use them, for evaluation. */
function evaluationContext(parameters: readonly Parameter[]): Record<string, number | boolean | string> {
  const context: Record<string, number | boolean | string> = {};
  for (const parameter of parameters) {
    context[parameter.key] = placeholder(parameter);
  }
  return context;
}

function placeholder(parameter: Parameter): number | boolean | string {
  const value: ParameterValue | null | undefined = parameter.defaultValue;
  if (value) return value.value;
  switch (parameter.type) {
    case "integer":
    case "double":
      return 1;
    case "boolean":
      return false;
    case "string":
      return "";
  }
}

interface Props {
  readonly collectionId: string;
  readonly expression: string;
  readonly parameters: readonly Parameter[];
  readonly onChange: (expression: string) => void;
}

/**
 * A formula field that says what the formula does and where it is wrong.
 *
 * Validation is the shared engine's, over the wire: the same parser and evaluator
 * the phone runs, so a formula that reads as valid here cannot fail there. The
 * request is debounced, and a stale answer is dropped rather than shown — typing
 * must never be blocked by a slow response.
 */
export function FormulaField({ collectionId, expression, parameters, onChange }: Props) {
  const [check, setCheck] = useState<FormulaCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    if (expression.trim() === "") {
      setCheck(null);
      return;
    }
    setChecking(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await api.checkFormula(collectionId, expression, evaluationContext(parameters));
          if (generation.current === mine) setCheck(result);
        } catch {
          // A failed check is not a broken formula; leave the last verdict alone.
        } finally {
          if (generation.current === mine) setChecking(false);
        }
      })();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [collectionId, expression, parameters]);

  const unknown = (check?.identifiers ?? []).filter(
    (identifier) => !parameters.some((parameter) => parameter.key.toLowerCase() === identifier.toLowerCase()),
  );

  return (
    <div className={stack}>
      <input
        className={check && !check.isValid ? `${input} ${invalid}` : input}
        value={expression}
        onChange={(event) => onChange(event.target.value)}
        placeholder="people * min(days, 5) * 2"
        autoComplete="off"
        spellCheck={false}
        aria-invalid={check ? !check.isValid : false}
        aria-describedby="formula-status"
      />

      {check?.position != null ? (
        // A squiggle needs the exact column, and the parser reports it.
        <span className={caret} aria-hidden="true">
          {`${" ".repeat(Math.max(0, check.position))}^`}
        </span>
      ) : null}

      <div id="formula-status" className={row} aria-live="polite">
        {checking && !check ? <span className={muted}>Checking…</span> : null}
        {check?.isValid ? (
          <>
            <span className={muted}>Evaluates to</span>
            <span className={value}>{check.value}</span>
            <span className={muted}>with the current defaults</span>
          </>
        ) : null}
        {check && !check.isValid ? <span className={errorText}>{check.message}</span> : null}
      </div>

      {unknown.length > 0 ? (
        <span className={muted}>
          Not a parameter here: {unknown.join(", ")}. Add it to this template, or fix the spelling.
        </span>
      ) : null}

      {parameters.length > 0 ? (
        <div className={stack}>
          <span className={muted}>Parameters in scope (inherited included):</span>
          <div className={suggestions}>
            {parameters.map((parameter) => (
              <button
                key={parameter.id}
                type="button"
                className={suggestion}
                onClick={() => onChange(appendIdentifier(expression, parameter.key))}
                title={`Insert ${parameter.key}`}
              >
                {parameter.key}
              </button>
            ))}
            {["min(", "max(", "ceil(", "floor("].map((fn) => (
              <button key={fn} type="button" className={suggestion} onClick={() => onChange(expression + fn)}>
                {fn}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <span className={muted}>This template has no parameters yet, so a formula can only use numbers.</span>
      )}
    </div>
  );
}

/** Appends with a space when the expression does not already end in an operator. */
function appendIdentifier(expression: string, key: string): string {
  const trimmed = expression.trimEnd();
  if (trimmed === "" || /[+\-*/(,]$/.test(trimmed)) return `${trimmed}${trimmed === "" ? "" : " "}${key}`;
  return `${trimmed} * ${key}`;
}
